import React, { useState, useRef, useEffect } from "react";
import { FiChevronLeft, FiChevronRight, FiSettings, FiUser, FiMoon, FiLogOut, FiMail } from "react-icons/fi";
import logo_webp from "/logo_full.webp";
import { useAuthContext } from "../hooks/AuthContext";
import { useTenant } from "../hooks/useTenant";

function NavBar({ setSidebarCollapsed, sidebarCollapsed }) {
    const { logout, user } = useAuthContext();
    const { logo, name } = useTenant();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    
    // Extraer iniciales o nombre del usuario
    const userName = user?.displayName || user?.email?.split('@')[0] || 'Usuario';
    const userEmail = user?.email || 'sin-correo@legalcheck.com';
    const userRole = (user?.email?.endsWith('@prestanza.com')) ? 'Administrador' : 'Cliente';

    // Cerrar menú al hacer click fuera
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <nav className="bg-[#0f172a] border-b border-slate-800 fixed top-0 left-0 right-0 z-50 h-16">
            <div className="mx-auto px-4 md:px-6 lg:px-8 h-full">
                <div className="flex h-full items-center justify-between">
                    {/* Left section: Logo and Toggle */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center md:hidden">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors focus:outline-none"
                                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                aria-expanded={!sidebarCollapsed}
                                aria-controls="sidebar-menu"
                            >
                                <span className="sr-only">{sidebarCollapsed ? "Abrir menú lateral" : "Cerrar menú lateral"}</span>
                                {sidebarCollapsed ? (
                                    <svg className="block w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                                    </svg>
                                ) : (
                                    <svg className="block w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        
                        <div className="flex shrink-0 items-center">
                            <img className="h-8 w-auto brightness-0 invert" src={logo || logo_webp} alt={name || "Logo"} />
                        </div>

                        <div className="hidden md:block ml-4">
                            <button
                                type="button"
                                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                className="flex items-center justify-center p-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-all duration-200 border border-slate-700"
                                title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                            >
                                {sidebarCollapsed ? (
                                    <FiChevronRight className="w-5 h-5" />
                                ) : (
                                    <FiChevronLeft className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Right section: User profile and Settings Dropdown */}
                    <div className="flex items-center gap-3 md:gap-6">
                        <div className="h-8 w-[1px] bg-slate-800 hidden md:block"></div>
                        <div className="relative" ref={menuRef}>
                            <button
                                type="button"
                                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 focus:outline-none border
                                    ${isMenuOpen 
                                        ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20' 
                                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white'
                                    }`}
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                title="Configuración de usuario"
                            >
                                <FiSettings className={`w-5 h-5 transition-transform duration-300 ${isMenuOpen ? 'rotate-90' : ''}`} />
                            </button>

                            {/* Dropdown Menu */}
                            {isMenuOpen && (
                                <div className="absolute right-0 mt-3 w-72 bg-[#1e293b] border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                    {/* User Header */}
                                    <div className="p-4 border-b border-slate-700 bg-slate-800/50">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                                                {userName.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex flex-col overflow-hidden">
                                                <span className="text-sm font-bold text-white truncate capitalize">{userName}</span>
                                                <div className="flex items-center gap-1.5 text-slate-400 mt-0.5">
                                                    <FiMail className="w-3 h-3 flex-shrink-0" />
                                                    <span className="text-[11px] truncate leading-none">{userEmail}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Options */}
                                    <div className="p-1.5">
                                        <button 
                                            disabled
                                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-500 cursor-not-allowed rounded-lg transition-colors group"
                                        >
                                            <FiMoon className="w-4 h-4" />
                                            <div className="flex flex-1 items-center justify-between">
                                                <span>Cambiar tema</span>
                                                <span className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-400">Próximamente</span>
                                            </div>
                                        </button>

                                        <button 
                                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-white rounded-lg transition-colors group"
                                        >
                                            <FiUser className="w-4 h-4 text-slate-400 group-hover:text-blue-400" />
                                            <span>Mi Perfil</span>
                                        </button>

                                        <div className="my-1 border-t border-slate-700/50"></div>

                                        <button 
                                            onClick={() => {
                                                setIsMenuOpen(false);
                                                if (globalThis.confirm('¿Seguro que deseas cerrar sesión?')) {
                                                    logout();
                                                }
                                            }}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-colors group"
                                        >
                                            <FiLogOut className="w-4 h-4" />
                                            <span>Cerrar Sesión</span>
                                        </button>
                                    </div>

                                    {/* Footer */}
                                    <div className="px-4 py-2 bg-slate-900/50 border-t border-slate-700 text-[10px] text-slate-500 text-center italic">
                                        Conectado como {userRole}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}


export default NavBar; 